/* eslint-disable @typescript-eslint/ban-ts-comment */
import { AUTH_PROFILE_PICTURE } from '../../utils/globals';
import { useAuthFetch } from '../use-auth-fetch';
import type { IdkConfig } from '../use-env-config';
import { useRegion } from '../use-region';

const imageSize = {
  small: 24,
  medium: 48,
  large: 96,
};

type ProfilePicture = {
  id: string;
  src?: string;
};

export interface Profile {
  id: string;
  src: string | undefined;
  loading: boolean;
  error: string | null;
}

/**
 * Fetches the user's profile picture and returns it as a URL in the shape of
 * `{ id: string, src: string (base64 encoded) | undefined, loading: boolean, error: string | null }`.
 * The `id` property is not currently used and is always an empty string.
 * The `src` property contains the URL of the user's profile picture, or undefined
 * if the user has no profile picture.
 * The `loading` property is a boolean indicating whether the data is currently loading.
 * The `error` property is a string indicating whether there was an error fetching the data.
 *
 * @param idk The IDK configuration object
 * @returns The user's profile picture
 */
export function useProfilePicture(idk: IdkConfig | undefined): Profile {
  const { region } = useRegion(idk);
  const {
    data: profilePicture,
    loading,
    error,
  }: {
    data: ProfilePicture | null;
    loading: boolean;
    error: string | null;
    refetch: () => void;
  } = useAuthFetch(`${AUTH_PROFILE_PICTURE.url}?idk_region=${region}&size=${imageSize.small}`, {
    method: 'GET',
    resourceHostId: AUTH_PROFILE_PICTURE.resourceHostId,
  });

  // @ts-expect-error
  const src: string = profilePicture ? profilePicture.profilePictureUrl : '';

  return { id: '', src, loading, error };
}
